import React, {PureComponent} from 'react';
import {IPageResponse, SortDirection} from "../common/interfaces/IPageResponse";
import { IPageRequest } from 'common/interfaces/IPageRequest';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { ICustomer } from 'components/ElectricityCustomers/model';
import { CustomerEnergyAgreement, EnergyAgreementSortFields } from 'components/EnergyAgreements/model';
import EnergyAgreementService from 'services/energy/energy-agreement.service';
import { EnergyAgreementsSearchBar } from 'components/EnergyAgreements/EnergyAgreementsSearchBar';
import { EnergyAgreementsTable } from 'components/EnergyAgreements/EnergyAgreementsTable';
import moment from 'moment';
import { isoDateFormat } from 'common/dateFormatterConfig';
import { EnergyPackageStatistic } from 'components/EnergyPackages/models';
import EnergyPackagesService from 'components/EnergyPackages/energy-packages.service';


export interface EnergyAgreementsPageRequest extends IPageRequest<EnergyAgreementSortFields> {
    isActive?: boolean;
    periodStart?: string;
    periodEnd?: string;
}

export interface EnergyAgreementsState {
    pageRequest: EnergyAgreementsPageRequest;
    isRequesting: boolean;
    isExporting: boolean;
    energyAgreements?: IPageResponse<CustomerEnergyAgreement>;
    energyPackageStatistics?: EnergyPackageStatistic[];
    isCustomerSettingsModalVisible?: boolean;
    isProductionTableModalVisible?: boolean;
    isCreateInvoicesModalVisible?: boolean;
    createInvoicesIsPerson?: boolean;
    activeCustomer?: ICustomer;
}

const searchApi = AwesomeDebouncePromise(
  request => EnergyAgreementService.getAllEnergyAgreements(request),
  300
);

export default class EnergyAgreements extends PureComponent<any, EnergyAgreementsState> {
    constructor(props: any) {
        super(props);
        this.state = {
            isRequesting: false,
            isExporting: false,
            pageRequest: {
                page: 0,
                size: 20,
                sortDirection: SortDirection.DESC,
                sortField: EnergyAgreementSortFields.ID,
                searchQuery: null,
                isActive: true
            }
        };
        this.props.onLoadValidatePage();
    }

    
    getEnergyAgreements = async () => {
        try {
            this.setState({ isRequesting: true });
            const res = await EnergyAgreementService.getAllEnergyAgreements(this.state.pageRequest);
            this.setState({
                energyAgreements: res,
            });
        } catch(e) {
            console.error(e);
        } finally {
            this.setState({ isRequesting: false });
        }
    };

    getEnergyPackageStatistics = async () => {
        try {
            const res = await EnergyPackagesService.getEnergyPackageStatistics();
            this.setState({energyPackageStatistics: res});
        } catch(e) {
            console.error(e);
        }
    };

    getEnergyAgreementsReport = async () => {
        try {
            this.setState({ isExporting: true });
            await EnergyAgreementService.getEnergyAgreementsReport();
        } catch(e) {
            console.error(e);
        } finally {
            this.setState({ isExporting: false });
        }
    }

    handleSearchChange = (searchQuery: string) => {
      this.setState({
          energyAgreements: undefined,
          pageRequest: {
              page: 0,
              size: this.state.pageRequest.size,
              sortDirection: this.state.pageRequest.sortDirection,
              sortField: this.state.pageRequest.sortField,
              isActive: this.state.pageRequest.isActive,
              searchQuery
          }
      });
      this.searchEnergyAgreements({
              page: 0,
              size: this.state.pageRequest.size,
              sortDirection: this.state.pageRequest.sortDirection,
              sortField: this.state.pageRequest.sortField,
              isActive: this.state.pageRequest.isActive,
              searchQuery
        });
    };

    searchEnergyAgreements = (request: any) => {
        this.setState({
            isRequesting: true
        });
        searchApi(request).then(res => {
            this.setState({
                energyAgreements: res, 
                isRequesting: false
            });
        })
        
    }; 

    onPageChange = (targetPage: number) => {
        this.setState({
            pageRequest: {
                ...this.state.pageRequest,
                page: targetPage - 1
            }
        }, () => 
          this.getEnergyAgreements()
        );
    };

    onStatusChange = (isActive?: boolean) => {
        this.setState({
            pageRequest: {
                ...this.state.pageRequest,
                isActive
            }
        }, () => 
          this.getEnergyAgreements()
        );
    };

    onPeriodChange = (periodStart?: string, periodEnd?: string) => {
        this.setState({
            pageRequest: {
                ...this.state.pageRequest,
                periodStart: periodStart ? moment(new Date(periodStart)).format(isoDateFormat) : undefined,
                periodEnd: periodEnd ? moment(new Date(periodEnd)).format(isoDateFormat) : undefined,
            }
        }, () => 
          this.getEnergyAgreements()
        );
    }

    handleSortClick = (sortField: EnergyAgreementSortFields) => {
      let sortDirection = SortDirection.ASC;
      if (this.state.pageRequest.sortField.match(sortField)) {
          sortDirection = this.state.pageRequest.sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC;
      }
      this.setState({
          pageRequest: {
              ...this.state.pageRequest,
              sortDirection,
              sortField
          }
      }, () => this.onPageChange(1))
    };
  
    componentDidMount(): void {
        this.getEnergyAgreements();
        this.getEnergyPackageStatistics();
    }

    render() {
        return (
            <div className="container">
                <div className="row">
                    <div className="col">
                        <EnergyAgreementsSearchBar
                          onSearchChange={this.handleSearchChange}
                          onStatusChange={this.onStatusChange}
                          onPeriodChange={this.onPeriodChange}
                          statistics={this.state.energyPackageStatistics}
                          isExporting={this.state.isExporting}
                          getReport={this.getEnergyAgreementsReport}
                        />
                        <EnergyAgreementsTable 
                          onPageChange={this.onPageChange}
                          isRequesting={this.state.isRequesting}
                          energyAgreements={this.state.energyAgreements}
                          onSort={this.handleSortClick}
                          sortDirection={this.state.pageRequest.sortDirection}
                          sortField={this.state.pageRequest.sortField}
                        />
                    </div>
                </div>
            </div>
        );
    }
}